import noop from "lodash/noop";
import React, { FC } from "react";
import styled from "styled-components";
import { Button, Theme } from "@120wateraudit/waterworks";
import { Form as FinalForm, Field, FieldRenderProps } from "react-final-form";
import { Form, Checkbox, Popup } from "semantic-ui-react";
import { RiInformation2Line } from "react-icons/ri";
import Error from "src/components/Error";
import TextField from "src/components/Form/TextField";
import { RadioCard } from "../../Ptd/RadioCard";
import { DatePicker } from "src/components/Form";
import { useInitialValues, useCreateSubmissionPeriod } from "./hooks";
import Dropdown from "src/components/Form/Dropdown";
import { SubmissionPeriodOverdueSetting } from "src/types/enums/SubmissionPeriodOverdueSetting";
import { validateFormValues, validationSchema } from "../validation";
import PrimaryButton from "src/components/PrimaryButton";

interface Props {
  refetch: any;
  toggleCreateMode: any;
}

const CreateSubmissionPeriod: FC<Props> = ({
  refetch = noop,
  toggleCreateMode = noop,
}) => {
  const initialValues = useInitialValues();

  const validate = validateFormValues(validationSchema);

  const onSuccess = (): void => {
    toggleCreateMode();
    refetch();
  };

  const { errors, onSubmit } = useCreateSubmissionPeriod(onSuccess);

  return (
    <Wrapper>
      <FinalForm
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit, valid, form }) => (
          <Form
            onSubmit={() => {
              void handleSubmit();
            }}
          >
            <div>
              <TopWrapper>
                <Header>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <h3 style={{ fontSize: "24px", fontWeight: "600" }}>
                      New Submission Period
                    </h3>
                    <div>
                      <AddNewButton
                        variant="primary"
                        style={{ marginRight: "10px" }}
                        type="submit"
                        onClick={() => {
                          if (!valid) {
                            const allFieldNames = [
                              "name",
                              "dueDate",
                              "lockedDate",
                              "submissionType",
                              "overdueSetting",
                            ];
                            allFieldNames.forEach((field) => {
                              form.blur(field); // Triggers 'touched' status so we show all errors
                            });
                          }
                        }}
                        isValid={valid}
                      >
                        Save
                      </AddNewButton>
                      <CloseButton onClick={toggleCreateMode} type="button">
                        Cancel
                      </CloseButton>
                    </div>
                  </div>
                  {errors && (
                    <Error style={{ margin: "12px 24px" }} messages={errors} />
                  )}
                </Header>
              </TopWrapper>
              <SubmissionPeriodsContainer>
                <Field<string> name="name">
                  {({ input, meta }) => (
                    <div>
                      <Title>Submission Name</Title>
                      <TextField
                        input={input}
                        meta={{
                          error: meta.error && meta.touched ? meta.error : null,
                        }}
                        wrapperStyling={{ margin: "0px" }}
                        placeholder="Enter name for submission period"
                      />
                    </div>
                  )}
                </Field>
                <Field<string> name="submissionType">
                  {({ input, meta }) => (
                    <div>
                      <Title>Submission Type</Title>
                      <Dropdown
                        input={input}
                        meta={{
                          error: meta.error && meta.touched ? meta.error : null,
                        }}
                        wrapperStyling={{ margin: "0px" }}
                        options={[
                          { text: "Inventory", value: "inventory" },
                          {
                            text: "Customer Notice Certifications",
                            value: "cnc",
                          },
                        ]}
                        placeholder="Select Submission Type"
                      />
                    </div>
                  )}
                </Field>

                <div style={{ display: "flex", width: "75%", gap: "16px" }}>
                  <div style={{ width: "50%" }}>
                    <Title>
                      Due Date{" "}
                      <Popup
                        trigger={
                          <RiInformation2Line
                            size="20px"
                            style={{ marginLeft: "7px" }}
                          />
                        }
                        content="The date used when calculating if Submissions are Overdue."
                        basic
                        position="bottom center"
                      />
                    </Title>
                    <Field name="dueDate">
                      {({ input, meta }) => (
                        <div>
                          <DatePicker
                            input={input}
                            meta={{
                              error:
                                meta.error && meta.touched ? meta.error : null,
                            }}
                            wrapperStyling={{ margin: "0px" }}
                            placeholder="Select Due Date"
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                  <div style={{ width: "50%" }}>
                    <Title>
                      Close Date (Optional){" "}
                      <Popup
                        trigger={
                          <RiInformation2Line
                            size="20px"
                            style={{ marginLeft: "7px" }}
                          />
                        }
                        content="The date that determines the end of the Submission Period."
                        basic
                        position="bottom center"
                      />
                    </Title>
                    <Field name="lockedDate">
                      {({ input, meta }) => (
                        <div>
                          <DatePicker
                            input={input}
                            meta={{
                              error:
                                meta.error && meta.touched ? meta.error : null,
                            }}
                            wrapperStyling={{ margin: "0px" }}
                            placeholder="Select Close Date"
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
                <StyledFieldContainer>
                  <Title>
                    Overdue Status{" "}
                    <Popup
                      trigger={
                        <RiInformation2Line
                          size="20px"
                          style={{ marginLeft: "7px" }}
                        />
                      }
                      content="The following field is used to determine how Overdue status will be calculated for submissions in this period."
                      basic
                      position="bottom center"
                    />
                  </Title>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: "24px",
                    }}
                  >
                    <Field<string>
                      name="overdueSetting"
                      initialValue={initialValues.overdueSetting}
                    >
                      {({
                        input,
                        meta,
                      }: FieldRenderProps<string, HTMLElement>) => (
                        <>
                          <RadioCard
                            title={"First Submission Date"}
                            description={
                              "Overdue status will be based on the date of the first submission in this period. "
                            }
                            selected={
                              input.value ===
                              SubmissionPeriodOverdueSetting.firstSubmissionDate
                            }
                            onClick={() => {
                              input.onChange(
                                SubmissionPeriodOverdueSetting.firstSubmissionDate,
                              );
                            }}
                          />
                          <RadioCard
                            title={"Submission Date"}
                            description={
                              "Overdue status will be based on the date of the most recent submission in this period."
                            }
                            selected={
                              input.value ===
                              SubmissionPeriodOverdueSetting.submissionDate
                            }
                            onClick={() => {
                              input.onChange(
                                SubmissionPeriodOverdueSetting.submissionDate,
                              );
                            }}
                          />
                          {meta.touched && meta.error && (
                            <ErrorText>{meta.error}</ErrorText>
                          )}
                        </>
                      )}
                    </Field>
                  </div>
                </StyledFieldContainer>
                <StyledFieldContainer>
                  <Title>
                    Submission Period Status{" "}
                    <Popup
                      trigger={
                        <RiInformation2Line
                          size="20px"
                          style={{ marginLeft: "7px" }}
                        />
                      }
                      content="This toggle is used to determine whether or not submissions can be received."
                      basic
                      position="bottom center"
                    />
                  </Title>
                  <Field<boolean> name="active" initialValue={true}>
                    {({ input }: FieldRenderProps<boolean>) => {
                      const val = input.value ? "true" : "false";
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 20,
                          }}
                        >
                          <Checkbox
                            defaultChecked={input.value}
                            value={val}
                            onChange={() => {
                              input.onChange(!input.value);
                            }}
                            toggle
                            aria-label="Toggle submission period active status"
                          />
                          <Text
                            style={{
                              color: input.value ? Theme.palette.badgeSuccessText : Theme.palette.badgeErrorText,
                              fontSize: "15px",
                            }}
                          >
                            {input.value ? "Open" : "Closed"}
                          </Text>
                        </div>
                      );
                    }}
                  </Field>
                </StyledFieldContainer>
              </SubmissionPeriodsContainer>
            </div>
          </Form>
        )}
      />
    </Wrapper>
  );
};

const Text = styled.span`
  font-weight: 400;
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  line-height: 21.6px;
`;

const StyledFieldContainer = styled.div<{ disabled?: boolean }>`
  width: 100%;
  max-width: 815px;
  ${({ disabled }) =>
    disabled &&
    `> *:not(.no-disable) {
      opacity: 0.25;
      pointer-events: none;
    }`}
`;

const Wrapper = styled.div`
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
`;
const SubmissionPeriodsContainer = styled.div`
  display: grid;
  gap: 30px;
`;

const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 48px;
`;

const ErrorText = styled.span`
  color: ${Theme.palette.badgeErrorText};
`;

const AddNewButton = styled(PrimaryButton)<{ isValid?: boolean }>`
  &:hover {
    opacity: 0.5;
  }
  font-size: 16px;
  font-weight: 600;
  border: 1px solid #ccc;
  border-radius: 4px;
  color: white;
`;

const CloseButton = styled(Button)<{ isValid?: boolean }>`
  font-size: 16px;
  font-weight: 600;
  background-color: ${Theme.palette.neutral700} !important;
  border-radius: 4px;
  color: ${Theme.palette.neutral900} !important;
`;

export default CreateSubmissionPeriod;
